import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { currentWeekStart, addDays, todayMel } from "@/lib/time";
import { MessageSquareWarning } from "lucide-react";

export default function StaffTimesheets() {
  const [mode, setMode] = useState("week");
  const [range, setRange] = useState({ start: currentWeekStart(), end: addDays(currentWeekStart(), 6) });
  const [customStart, setCustomStart] = useState(currentWeekStart());
  const [customEnd, setCustomEnd] = useState(todayMel());
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: todayMel(), what_incorrect: "", correct_time: "", explanation: "", shift_id: null });

  const applyMode = useCallback((m) => {
    setMode(m);
    if (m === "day") setRange({ start: todayMel(), end: todayMel() });
    else if (m === "week") setRange({ start: currentWeekStart(), end: addDays(currentWeekStart(), 6) });
    else setRange({ start: customStart, end: customEnd });
  }, [customStart, customEnd]);

  const load = useCallback(async () => {
    const { data } = await api.get("/shifts/me", { params: { start: range.start, end: range.end } });
    setShifts(data);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const submitCorrection = async () => {
    try {
      const { data } = await api.post("/corrections", form);
      toast.success(data.message);
      setOpen(false);
      setForm({ date: todayMel(), what_incorrect: "", correct_time: "", explanation: "", shift_id: null });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-timesheets">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold">My Timesheets</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="report-mistake-btn">
              <MessageSquareWarning className="h-4 w-4 mr-2" /> Report a Mistake
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="report-mistake-dialog">
            <DialogHeader><DialogTitle>Report a Mistake</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mistake-date" />
              </div>
              <div className="space-y-2">
                <Label>What is incorrect?</Label>
                <Input value={form.what_incorrect} onChange={(e) => setForm({ ...form, what_incorrect: e.target.value })}
                  placeholder="e.g. My clock-out time" data-testid="mistake-what" />
              </div>
              <div className="space-y-2">
                <Label>Correct time</Label>
                <Input value={form.correct_time} onChange={(e) => setForm({ ...form, correct_time: e.target.value })}
                  placeholder="e.g. 4:30 PM" data-testid="mistake-correct-time" />
              </div>
              <div className="space-y-2">
                <Label>Short explanation</Label>
                <Textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                  placeholder="Briefly explain what happened" data-testid="mistake-explanation" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submitCorrection} disabled={!form.what_incorrect || !form.correct_time} data-testid="mistake-submit">
                Send to Administrator
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <Tabs value={mode} onValueChange={applyMode}>
          <TabsList data-testid="timesheet-mode-tabs">
            <TabsTrigger value="day" data-testid="mode-day">Day</TabsTrigger>
            <TabsTrigger value="week" data-testid="mode-week">Week</TabsTrigger>
            <TabsTrigger value="custom" data-testid="mode-custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>
        {mode === "custom" && (
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end" />
            </div>
            <Button onClick={() => setRange({ start: customStart, end: customEnd })} data-testid="apply-custom">Apply</Button>
          </div>
        )}
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Clock In</TableHead>
              <TableHead>Clock Out</TableHead>
              <TableHead>Break</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="timesheet-rows">
            {shifts.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No shifts in this period.</TableCell></TableRow>
            )}
            {shifts.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.date_display}</TableCell>
                <TableCell>{s.clock_in_display || "—"}</TableCell>
                <TableCell>{s.clock_out_display || "—"}</TableCell>
                <TableCell>
                  {s.total_break_display}
                  {s.breaks?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5" data-testid={`break-log-${s.id}`}>
                      {s.breaks.map((b, i) => (
                        <div key={i}>{b.start_display}–{b.end_display || "…"} ({b.minutes_display})</div>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-semibold">{s.total_worked_display}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
